/**
 * TimeOfDayClock Widget
 *
 * Real-time clock widget for the banner bar. Displays the current time
 * in a configurable timezone with digital or analog display modes.
 * Clicking the clock opens a settings popover for clock style, show seconds,
 * timezone, time format, font size, font color, and timezone label overrides.
 *
 * Settings resolution chain (per field):
 * - clockStyle: widgetConfigs.clock.clockStyle → LiveTradingSettings.clockStyle → 'digital'
 * - showSeconds: widgetConfigs.clock.showSeconds → true
 * - timezone: widgetConfigs.clock.timezone → BasicSettings.displayTimezone → browser
 * - timeFormat: widgetConfigs.clock.timeFormat → BasicSettings.timeFormat → '24h'
 * - fontSize: widgetConfigs.clock.fontSize → 'xs'
 * - fontColor: widgetConfigs.clock.fontColor → undefined (theme text-primary)
 * - showTimezoneLabel: widgetConfigs.clock.showTimezoneLabel → false
 *
 * Invalid stored overrides silently fall back to the default.
 *
 * Ticking pattern follows ElapsedTimer / CODING_STANDARDS §2.2:
 * - Shared useSecondPulse hook provides second-boundary aligned ticks
 * - Refs synced in render body (not in effects) for stable callback access
 * - React.memo boundary contains per-second re-renders
 *
 * @see components/TradingHub/ElapsedTimer.tsx — reference ticking pattern
 * @see types/shared/BannerWidget.ts — BannerWidgetComponentProps, ClockWidgetConfig
 * @see services/BasicSettingsService.ts — displayTimezone, timeFormat defaults
 * @see services/LiveTradingSettingsService.ts — clockStyle setting
 * @see constants/timezone.ts — TIMEZONES list for settings popover
 * @see constants/colors.ts — CATEGORY_COLORS palette for font color swatches
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Clock, RotateCcw, Check } from 'lucide-react';

// 2. Internal absolute imports
import {
  Popover, PopoverTrigger, PopoverContent,
  Autocomplete, AutocompleteItem,
  ButtonGroup, Button, Switch,
  Card, CardBody,
} from '@enfomentis/ui';
import { CATEGORY_COLORS } from '@/constants/colors';
import { TIMEZONES } from '@/constants/timezone';
import { useSecondPulse } from '@/hooks';
import { basicSettingsService } from '@/services/BasicSettingsService';
import { liveTradingSettingsService } from '@/services/LiveTradingSettingsService';

import type { BannerWidgetComponentProps } from '@/types/shared/BannerWidget';

// ===== CONSTANTS =====

/** Browser's local timezone (computed once at module scope) */
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Default time format when no setting is available */
const DEFAULT_TIME_FORMAT: '12h' | '24h' = '24h';

/** Default clock style when no setting is available */
const DEFAULT_CLOCK_STYLE: 'analog' | 'digital' = 'digital';

/** Default font size */
const DEFAULT_FONT_SIZE: 'xs' | 'sm' | 'base' = 'xs';

/** Clock style options for the ButtonGroup */
const CLOCK_STYLE_OPTIONS = [
  { label: 'Digital', value: 'digital' },
  { label: 'Analog', value: 'analog' },
] as const;

/** Time format options for the ButtonGroup */
const TIME_FORMAT_OPTIONS = [
  { label: '12h', value: '12h' },
  { label: '24h', value: '24h' },
] as const;

/** Font size options for the ButtonGroup */
const FONT_SIZE_OPTIONS = [
  { label: 'XS', value: 'xs' },
  { label: 'SM', value: 'sm' },
  { label: 'Base', value: 'base' },
] as const;

/** Analog clock SVG size (px) per font size setting */
const ANALOG_SIZE_MAP: Readonly<Record<'xs' | 'sm' | 'base', number>> = {
  xs: 28,
  sm: 32,
  base: 36,
};

/** Digital clock Tailwind class per font size setting */
const FONT_SIZE_CLASS_MAP: Readonly<Record<'xs' | 'sm' | 'base', string>> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
};

/** Palette entry — `value` is persisted, `css` is applied at render time */
interface ClockColorEntry {
  name: string;
  /** Stored in fontColor: token key for theme colors, hex for fixed palette */
  value: string;
  /** CSS color string: var(--color-*) for tokens, hex for fixed palette */
  css: string;
}

/** Clock color palette — theme tokens + fixed CATEGORY_COLORS */
const CLOCK_COLOR_PALETTE: readonly ClockColorEntry[] = [
  { name: 'Primary', value: 'primary', css: 'var(--color-primary)' },
  { name: 'Secondary', value: 'secondary', css: 'var(--color-secondary)' },
  ...CATEGORY_COLORS.map((c) => ({ name: c.name, value: c.value, css: c.value })),
];

/** Stored value → CSS value lookup for render-time resolution */
const FONT_COLOR_CSS_MAP: ReadonlyMap<string, string> = new Map(
  CLOCK_COLOR_PALETTE.map((c) => [c.value, c.css]),
);

/** Valid stored values for quick validation */
const VALID_FONT_COLORS: ReadonlySet<string> = new Set(
  CLOCK_COLOR_PALETTE.map((c) => c.value),
);

/** Legacy hex → token key aliases (FU1 stored hex for Primary/Secondary) */
const LEGACY_HEX_TO_TOKEN: Readonly<Record<string, string>> = {
  '#0ea5e9': 'primary',
  '#14b8a6': 'secondary',
};

// ===== HELPERS =====

/** Validate an IANA timezone string */
function isValidTimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Validate a clock style string */
function isValidClockStyle(value: unknown): value is 'analog' | 'digital' {
  return value === 'analog' || value === 'digital';
}

/** Validate a time format string */
function isValidTimeFormat(fmt: unknown): fmt is '12h' | '24h' {
  return fmt === '12h' || fmt === '24h';
}

/** Validate a font size string against allowed values */
function isValidFontSize(value: unknown): value is 'xs' | 'sm' | 'base' {
  return value === 'xs' || value === 'sm' || value === 'base';
}

/** Normalize a stored fontColor — maps legacy hex to token key, passes others through */
function normalizeFontColor(value: string): string {
  return LEGACY_HEX_TO_TOKEN[value] ?? value;
}

/** Validate a font color string against the palette (after normalization) */
function isValidFontColor(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return VALID_FONT_COLORS.has(normalizeFontColor(value));
}

/** Settings loaded from services (null while loading) */
interface LoadedSettings {
  timezone: string;
  timeFormat: '12h' | '24h';
  clockStyle: 'analog' | 'digital';
}

// ===== ANALOG CLOCK SUB-COMPONENT =====

interface AnalogClockFaceProps {
  now: Date;
  timezone: string;
  size: number;
  showSeconds: boolean;
}

/** Compact SVG analog clock face for the banner strip (scalable) */
const AnalogClockFace: React.FC<AnalogClockFaceProps> = ({ now, timezone, size, showSeconds }) => {
  const center = size / 2;
  const radius = center - 1;
  const tickInner = radius * (10 / 13);
  const tickOuter = radius * (12 / 13);
  const hourHandEnd = center - (center * (8 / 14));
  const minuteHandEnd = center - (center * (10 / 14));
  const secondHandEnd = center - (center * (11 / 14));

  // Extract time parts in the target timezone
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).formatToParts(now);

  const hours = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minutes = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  const seconds = Number(parts.find((p) => p.type === 'second')?.value ?? 0);

  // Hand angles (0° = 12 o'clock position)
  const secondAngle = seconds * 6;
  const minuteAngle = minutes * 6 + seconds * 0.1;
  const hourAngle = (hours % 12) * 30 + minutes * 0.5;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      {/* Clock face outline */}
      <circle cx={center} cy={center} r={radius} fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3" />

      {/* Hour tick marks */}
      {Array.from({ length: 12 }, (_, i) => {
        const angle = ((i * 30) - 90) * (Math.PI / 180);
        return (
          <line
            key={i}
            x1={center + tickInner * Math.cos(angle)}
            y1={center + tickInner * Math.sin(angle)}
            x2={center + tickOuter * Math.cos(angle)}
            y2={center + tickOuter * Math.sin(angle)}
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.5"
          />
        );
      })}

      {/* Hour hand */}
      <line
        x1={center} y1={center} x2={center} y2={hourHandEnd}
        stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        transform={`rotate(${hourAngle}, ${center}, ${center})`}
      />

      {/* Minute hand */}
      <line
        x1={center} y1={center} x2={center} y2={minuteHandEnd}
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
        transform={`rotate(${minuteAngle}, ${center}, ${center})`}
      />

      {/* Second hand */}
      {showSeconds && (
        <line
          x1={center} y1={center} x2={center} y2={secondHandEnd}
          stroke="var(--color-danger-500)" strokeWidth="0.5" strokeLinecap="round"
          transform={`rotate(${secondAngle}, ${center}, ${center})`}
        />
      )}

      {/* Center dot */}
      <circle cx={center} cy={center} r={1.5} fill="currentColor" />
    </svg>
  );
};

// ===== MAIN COMPONENT =====

const TimeOfDayClockInner: React.FC<BannerWidgetComponentProps> = ({ widgetConfigs, onClockConfigChange }) => {
  const [settings, setSettings] = useState<LoadedSettings | null>(null);
  const [now, setNow] = useState<Date>(() => new Date());
  const isPopoverOpenRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Load settings on mount (Promise.allSettled for partial-success resilience)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      const [basicResult, liveResult] = await Promise.allSettled([
        basicSettingsService.getOrCreateDefaultSettings(),
        liveTradingSettingsService.getOrCreateDefaultSettings(),
      ]);

      if (cancelled) return;

      const basic = basicResult.status === 'fulfilled' ? basicResult.value : null;
      const live = liveResult.status === 'fulfilled' ? liveResult.value : null;

      setSettings({
        timezone: basic?.displayTimezone ?? BROWSER_TIMEZONE,
        timeFormat: isValidTimeFormat(basic?.timeFormat) ? basic.timeFormat : DEFAULT_TIME_FORMAT,
        clockStyle: live?.clockStyle ?? DEFAULT_CLOCK_STYLE,
      });
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Resolve all settings with fallback chains
  // ---------------------------------------------------------------------------

  const clockConfig = widgetConfigs?.clock;

  // Timezone: override → BasicSettings → browser
  const resolvedTimezone =
    (clockConfig?.timezone && isValidTimezone(clockConfig.timezone))
      ? clockConfig.timezone
      : (settings?.timezone && isValidTimezone(settings.timezone))
        ? settings.timezone
        : BROWSER_TIMEZONE;

  // Time format: override → BasicSettings → '24h'
  const resolvedTimeFormat =
    isValidTimeFormat(clockConfig?.timeFormat)
      ? clockConfig.timeFormat
      : isValidTimeFormat(settings?.timeFormat)
        ? settings.timeFormat
        : DEFAULT_TIME_FORMAT;

  // Clock style: override → LiveTradingSettings → 'digital'
  const resolvedClockStyle =
    isValidClockStyle(clockConfig?.clockStyle)
      ? clockConfig.clockStyle
      : settings?.clockStyle ?? DEFAULT_CLOCK_STYLE;

  // Font size: override → 'xs'
  const resolvedFontSize = isValidFontSize(clockConfig?.fontSize) ? clockConfig.fontSize : DEFAULT_FONT_SIZE;

  // Font color: override (normalized) → undefined (theme default)
  const resolvedFontColor = isValidFontColor(clockConfig?.fontColor)
    ? normalizeFontColor(clockConfig.fontColor)
    : undefined;

  // Timezone label: override → false
  const resolvedShowTimezoneLabel = clockConfig?.showTimezoneLabel === true;

  // Show seconds: override → true (missing ≡ true)
  const resolvedShowSeconds = clockConfig?.showSeconds !== false;

  // Determine which values are overrides vs defaults
  const hasTimezoneOverride = !!(clockConfig?.timezone && isValidTimezone(clockConfig.timezone));
  const hasTimeFormatOverride = isValidTimeFormat(clockConfig?.timeFormat);
  const hasClockStyleOverride = isValidClockStyle(clockConfig?.clockStyle);
  const hasFontSizeOverride = isValidFontSize(clockConfig?.fontSize);
  const hasFontColorOverride = isValidFontColor(clockConfig?.fontColor);
  const hasTimezoneLabelOverride = clockConfig?.showTimezoneLabel === true;
  const hasShowSecondsOverride = clockConfig?.showSeconds === false;
  const hasAnyOverride = hasTimezoneOverride || hasTimeFormatOverride || hasClockStyleOverride || hasFontSizeOverride || hasFontColorOverride || hasTimezoneLabelOverride || hasShowSecondsOverride;

  // ---------------------------------------------------------------------------
  // Immediate display on mount — no blank first render
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isPopoverOpenRef.current) {
      setNow(new Date());
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Second-boundary aligned ticks via shared pulse (CODING_STANDARDS §2.2)
  // Uses isPopoverOpenRef to pause ticking while settings popover is open,
  // preventing re-renders that disrupt Autocomplete keyboard navigation.
  // Timezone/format resolution happens in the render body, so resolved
  // values are always current when formatting.
  // ---------------------------------------------------------------------------

  useSecondPulse((tickAtMs) => {
    if (!isPopoverOpenRef.current) {
      setNow(new Date(tickAtMs));
    }
  });

  // ---------------------------------------------------------------------------
  // Memoized formatters (recreated only when timezone/format changes)
  // ---------------------------------------------------------------------------

  const formatter = useMemo(
    () => new Intl.DateTimeFormat('en-US', {
      timeZone: resolvedTimezone,
      hour: '2-digit',
      minute: '2-digit',
      ...(resolvedShowSeconds ? { second: '2-digit' as const } : {}),
      hour12: resolvedTimeFormat === '12h',
    }),
    [resolvedTimezone, resolvedTimeFormat, resolvedShowSeconds],
  );

  // Timezone abbreviation (e.g., "EST", "PST", "UTC") for the label
  const timezoneAbbrev = useMemo(() => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: resolvedTimezone,
        timeZoneName: 'short',
      }).formatToParts(now);
      return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      return '';
    }
  }, [resolvedTimezone, now]);

  // ---------------------------------------------------------------------------
  // Settings popover callbacks — spread-from-existing pattern
  // All handlers spread the current clockConfig to preserve sibling overrides,
  // then set/clear the one field being changed.
  // ---------------------------------------------------------------------------

  const handleTimezoneChange = useCallback((key: React.Key | null) => {
    if (!onClockConfigChange) return;

    if (key === null) {
      // User cleared the timezone selection — remove timezone override
      if (clockConfig) {
        const { timezone: _removed, ...rest } = clockConfig;
        onClockConfigChange(Object.keys(rest).length > 0 ? rest : undefined);
      } else {
        onClockConfigChange(undefined);
      }
      return;
    }

    const tz = String(key);
    onClockConfigChange({ ...clockConfig, timezone: tz });
  }, [onClockConfigChange, clockConfig]);

  const handleClockStyleChange = useCallback((value: string | number | (string | number)[] | null) => {
    if (!onClockConfigChange) return;
    if (value !== 'analog' && value !== 'digital') return;
    onClockConfigChange({ ...clockConfig, clockStyle: value });
  }, [onClockConfigChange, clockConfig]);

  const handleTimeFormatChange = useCallback((value: string | number | (string | number)[] | null) => {
    if (!onClockConfigChange) return;
    if (value !== '12h' && value !== '24h') return;
    onClockConfigChange({ ...clockConfig, timeFormat: value });
  }, [onClockConfigChange, clockConfig]);

  const handleFontSizeChange = useCallback((value: string | number | (string | number)[] | null) => {
    if (!onClockConfigChange) return;
    if (value !== 'xs' && value !== 'sm' && value !== 'base') return;
    onClockConfigChange({ ...clockConfig, fontSize: value });
  }, [onClockConfigChange, clockConfig]);

  const handleFontColorChange = useCallback((color: string | undefined) => {
    if (!onClockConfigChange) return;
    if (color === undefined) {
      // Clear color override — remove fontColor key
      if (clockConfig) {
        const { fontColor: _removed, ...rest } = clockConfig;
        onClockConfigChange(Object.keys(rest).length > 0 ? rest : undefined);
      } else {
        onClockConfigChange(undefined);
      }
    } else {
      onClockConfigChange({ ...clockConfig, fontColor: color });
    }
  }, [onClockConfigChange, clockConfig]);

  const handleTimezoneLabelToggle = useCallback((show: boolean) => {
    if (!onClockConfigChange) return;
    if (show) {
      onClockConfigChange({ ...clockConfig, showTimezoneLabel: true });
    } else {
      // Remove key rather than persisting false (default = false)
      if (clockConfig) {
        const { showTimezoneLabel: _removed, ...rest } = clockConfig;
        onClockConfigChange(Object.keys(rest).length > 0 ? rest : undefined);
      } else {
        onClockConfigChange(undefined);
      }
    }
  }, [onClockConfigChange, clockConfig]);

  const handleShowSecondsToggle = useCallback((show: boolean) => {
    if (!onClockConfigChange) return;
    if (!show) {
      onClockConfigChange({ ...clockConfig, showSeconds: false });
    } else {
      // Remove key rather than persisting true (default = true)
      if (clockConfig) {
        const { showSeconds: _removed, ...rest } = clockConfig;
        onClockConfigChange(Object.keys(rest).length > 0 ? rest : undefined);
      } else {
        onClockConfigChange(undefined);
      }
    }
  }, [onClockConfigChange, clockConfig]);

  const handleRevertToDefaults = useCallback(() => {
    onClockConfigChange?.(undefined);
  }, [onClockConfigChange]);

  // ---------------------------------------------------------------------------
  // Render — clock display
  // ---------------------------------------------------------------------------

  const resolvedFontColorCss = resolvedFontColor
    ? (FONT_COLOR_CSS_MAP.get(resolvedFontColor) ?? resolvedFontColor)
    : undefined;
  const colorStyle = resolvedFontColorCss ? { color: resolvedFontColorCss } : undefined;
  const fontSizeClass = FONT_SIZE_CLASS_MAP[resolvedFontSize];
  const analogSize = ANALOG_SIZE_MAP[resolvedFontSize];

  const clockDisplay = resolvedClockStyle === 'analog' ? (
    <div className="flex items-center gap-1.5 whitespace-nowrap text-text-primary" style={colorStyle}>
      <AnalogClockFace now={now} timezone={resolvedTimezone} size={analogSize} showSeconds={resolvedShowSeconds} />
      {resolvedShowTimezoneLabel && timezoneAbbrev && (
        <span className={`${fontSizeClass} font-mono font-medium opacity-60`}>{timezoneAbbrev}</span>
      )}
    </div>
  ) : (
    <div className="flex items-center gap-1.5 whitespace-nowrap text-text-primary" style={colorStyle}>
      <Clock size={14} className={hasFontColorOverride ? 'shrink-0' : 'shrink-0 text-default-400'} />
      <span className={`${fontSizeClass} font-mono font-medium tabular-nums`}>
        {formatter.format(now)}
      </span>
      {resolvedShowTimezoneLabel && timezoneAbbrev && (
        <span className={`${fontSizeClass} font-mono font-medium tabular-nums opacity-90`}>{timezoneAbbrev}</span>
      )}
    </div>
  );

  // If no config change handler, render clock without popover (read-only mode)
  if (!onClockConfigChange) {
    return clockDisplay;
  }

  return (
    <Popover
      placement="bottom"
      onOpenChange={(isOpen: boolean) => {
        isPopoverOpenRef.current = isOpen;
        if (!isOpen) {
          // Catch up immediately when popover closes so clock resumes.
          setNow(new Date());
        }
      }}
    >
      <PopoverTrigger>
        <button
          type="button"
          className="cursor-pointer rounded-md px-1.5 py-1 transition-colors hover:bg-default-100 focus-visible:outline-2 focus-visible:outline-primary flex items-center justify-center"
          aria-label="Clock settings"
        >
          {clockDisplay}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-3 w-72">
        <div className="flex flex-col gap-2">
          {/* Header */}
          <span className="text-xs font-semibold text-text-primary">Clock Settings</span>

          {/* ── Display group ── */}
          <Card radius="none" shadow="none" className="w-full border-t-1 border-default-200">
            <CardBody className="px-0 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-default-600">Clock Style</span>
                  <ButtonGroup
                    options={[...CLOCK_STYLE_OPTIONS]}
                    value={resolvedClockStyle}
                    onChange={handleClockStyleChange}
                    size="sm"
                    variant="flat"
                    aria-label="Clock display style"
                  />
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <span className="text-xs text-default-600">Show Seconds</span>
                  <Switch
                    size="sm"
                    isSelected={resolvedShowSeconds}
                    onValueChange={handleShowSecondsToggle}
                    aria-label="Show seconds"
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* ── Time group ── */}
          <Card radius="none" shadow="none" className="w-full border-t-1 border-default-200">
            <CardBody className="px-0 py-2 gap-3">
              {/* Timezone selector */}
              <div className="flex flex-col gap-1">
                <Autocomplete
                  label="Timezone"
                  labelPlacement="outside"
                  placeholder="Search timezones..."
                  size="sm"
                  variant="bordered"
                  selectedKey={hasTimezoneOverride ? clockConfig?.timezone ?? null : null}
                  isClearable
                  menuTrigger="input"
                  onSelectionChange={handleTimezoneChange}
                  aria-label="Clock timezone override"
                  className="max-md:[&_input]:text-base!"
                >
                  {TIMEZONES.map((tz) => (
                    <AutocompleteItem key={tz.value}>
                      {tz.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
                {!hasTimezoneOverride && (
                  <span className="text-[10px] text-default-400">
                    Using: {resolvedTimezone} (default)
                  </span>
                )}
              </div>
              {/* Time format + Timezone label row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-default-600">Time Format</span>
                  <ButtonGroup
                    options={[...TIME_FORMAT_OPTIONS]}
                    value={resolvedTimeFormat}
                    onChange={handleTimeFormatChange}
                    size="sm"
                    variant="flat"
                    aria-label="Clock time format"
                  />
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <span className="text-xs text-default-600">TZ Label</span>
                  <Switch
                    size="sm"
                    isSelected={resolvedShowTimezoneLabel}
                    onValueChange={handleTimezoneLabelToggle}
                    aria-label="Show timezone label"
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* ── Appearance group ── */}
          <Card radius="none" shadow="none" className="w-full border-t-1 border-default-200">
            <CardBody className="px-0 py-2 gap-3">
              {/* Font size selector */}
              <div className="flex flex-col gap-1">
                <span className="text-xs text-default-600">Font Size</span>
                <ButtonGroup
                  options={[...FONT_SIZE_OPTIONS]}
                  value={resolvedFontSize}
                  onChange={handleFontSizeChange}
                  size="sm"
                  variant="flat"
                  aria-label="Clock font size"
                />
              </div>
              {/* Color swatches */}
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-default-600">Color</span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Default (theme) swatch */}
                  <button
                    type="button"
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      !hasFontColorOverride
                        ? 'border-primary ring-1 ring-primary/30'
                        : 'border-default-300 hover:border-default-400'
                    }`}
                    style={{ backgroundColor: 'var(--color-text-primary)' }}
                    onClick={() => handleFontColorChange(undefined)}
                    aria-label="Default theme color"
                    aria-pressed={!hasFontColorOverride}
                  >
                    {!hasFontColorOverride && <Check size={12} className="text-white" />}
                  </button>
                  {/* Palette swatches */}
                  {CLOCK_COLOR_PALETTE.map((color) => {
                    const isActive = resolvedFontColor === color.value;
                    return (
                      <button
                        key={color.value}
                        type="button"
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isActive
                            ? 'border-primary ring-1 ring-primary/30'
                            : 'border-default-300 hover:border-default-400'
                        }`}
                        style={{ backgroundColor: color.css }}
                        onClick={() => handleFontColorChange(color.value)}
                        aria-label={`${color.name} color`}
                        aria-pressed={isActive}
                      >
                        {isActive && <Check size={12} className="text-white" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Revert to defaults */}
          <Button
            variant="light"
            size="sm"
            startContent={<RotateCcw size={14} />}
            onPress={handleRevertToDefaults}
            isDisabled={!hasAnyOverride}
            className="self-start"
          >
            Revert to defaults
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const TimeOfDayClock = React.memo(TimeOfDayClockInner);

TimeOfDayClock.displayName = 'TimeOfDayClock';
